// @ts-strict-ignore
import {Component, inject, Injector, OnInit} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {FetchDataType, Media, ScheduledSound, ScheduledSoundOffsetType, SessionPhaseType, SimStatusPalette} from "@models";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {UntilDestroy} from "@ngneat/until-destroy";
import {DurationLabelPipe} from "@common/pipes/time/duration-label.pipe";
import {SimExamFlowService} from "@services";

export interface DialogData_CreateAnnouncementDialogInput extends MatDialogDataBaseInput {
	editAnnouncement?: ScheduledSound;
	sessionPhaseType: SessionPhaseType;
	examFlowId: string;
	duration: number;
}

export interface DialogData_CreateAnnouncementDialogOutput extends MatDialogDataBaseOutput {
	announcement: ScheduledSound;
}

@UntilDestroy()
@Component({
	selector: "sim-create-announcement-dialog",
	templateUrl: "./create-announcement-dialog.component.html",
	styleUrl: "./create-announcement-dialog.component.scss",
	providers: [DurationLabelPipe],
	standalone: false,
})
export class CreateAnnouncementDialogComponent
	extends BasicMaterialDialog<
		CreateAnnouncementDialogComponent,
		DialogData_CreateAnnouncementDialogInput,
		DialogData_CreateAnnouncementDialogOutput
	>
	implements OnInit
{
	// Properties
	public form = new FormGroup({
		name: new FormControl<string>("", Validators.required),
		media: new FormControl<Media>(null, Validators.required),
		offset: new FormControl<number>(0, [Validators.required]),
		offsetType: new FormControl<ScheduledSoundOffsetType>(ScheduledSoundOffsetType.FromStart, Validators.required),
	});
	public sliderStep: number;

	// Readonly
	protected readonly FetchDataType = FetchDataType;
	protected readonly SimStatusPalette = SimStatusPalette;
	protected readonly ScheduledSoundOffsetType = ScheduledSoundOffsetType;

	// Injections
	readonly #durationPipe = inject(DurationLabelPipe);
	readonly #examFlowService = inject(SimExamFlowService);

	constructor(_injector: Injector) {
		super(_injector);
	}

	public ngOnInit() {
		super.ngOnInit();
		this._determineSliderStep(this.data.duration);
		if (this.data.editAnnouncement) {
			this.form.controls.name.setValue(this.data.editAnnouncement.name);
			this.form.controls.media.setValue(this.data.editAnnouncement.mediaFile);
			this.form.controls.offset.setValue(this.data.editAnnouncement.offset);
			this.form.controls.offsetType.setValue(this.data.editAnnouncement.offsetType);
		}
	}

	protected formatLabel(): (_: number) => string {
		return (_: number) => this.#durationPipe.transform(this.form.value.offset);
	}

	protected async doSave(): Promise<void> {
		const scheduledSoundRequest = {
			name: this.form.value.name,
			offset: this.form.value.offset,
			offsetType: this.form.value.offsetType,
			mediaId: this.form.value.media.id,
		};

		const announcement = await (this.data.editAnnouncement
			? this.#examFlowService.updateAnnouncementOfExamFlowPhase(
					this.data.examFlowId,
					this.data.sessionPhaseType,
					this.data.editAnnouncement.id,
					scheduledSoundRequest,
				)
			: this.#examFlowService.addAnnouncementToExamFlowPhase(
					this.data.examFlowId,
					this.data.sessionPhaseType,
					scheduledSoundRequest,
				));
		this.close({action: "ok", announcement});
	}

	private _determineSliderStep(duration: number): void {
		const durationInMinutes = Math.floor(duration / 60_000);
		// <= 1 min
		if (durationInMinutes <= 1) {
			this.sliderStep = 10_000;
			return;
		}

		// 1 - 10 min
		if (durationInMinutes > 1 && durationInMinutes < 10) {
			this.sliderStep = 30_000;
			return;
		}

		// 10+ min
		if (durationInMinutes >= 10) {
			this.sliderStep = 60_000;
			return;
		}
	}
}
